
import { supabase } from '@/integrations/supabase/client';
import { CreateUserData } from '@/types/userManagement';
import { serviceLogger } from '@/utils/debug';
import { handleServiceError } from '@/utils/errorHandler';

export const authService = {
  async validateUserAccess(userId: string) {
    serviceLogger.log('🔥 Validating user access for:', userId);

    try {
      // Use select without .maybeSingle() to avoid problematic Accept header
      const { data, error } = await supabase
        .from('profiles')
        .select('is_active, role, full_name, email')
        .eq('id', userId)
        .limit(1);

      if (error) {
        handleServiceError(error, 'validateUserAccess: Database error');
      }

      // Handle array response
      const profile = data && data.length > 0 ? data[0] : null;

      if (!profile) {
        const err = new Error('Profile not found - Please contact your administrator');
        handleServiceError(err, 'validateUserAccess: No profile found');
      }

      if (!profile.is_active) {
        const err = new Error('Your account is currently inactive. Please contact your administrator.');
        handleServiceError(err, 'validateUserAccess: Account inactive');
      }

      serviceLogger.log('🔥 User access validated successfully for:', profile.full_name);
      return { isValid: true, profile };
    } catch (error) {
      handleServiceError(error, 'validateUserAccess');
    }
  },

  async updateLastLoginTimestamp(userId: string) {
    serviceLogger.log('🔥 Updating last login timestamp for user:', userId);

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ last_login_at: new Date().toISOString() })
        .eq('id', userId);

      if (error) {
        handleServiceError(error, 'updateLastLoginTimestamp');
      }

      serviceLogger.log('🔥 Last login timestamp updated successfully');
    } catch (error) {
      handleServiceError(error, 'updateLastLoginTimestamp');
    }
  },

  async createAuthUser(userData: CreateUserData) {
    serviceLogger.log('🔥 Creating auth user:', userData.email);

    try {
      const { data, error } = await supabase.auth.signUp({
        email: userData.email,
        password: userData.password,
        options: {
          data: {
            full_name: userData.fullName,
            role: userData.role,
          },
          emailRedirectTo: `${window.location.origin}/auth`
        },
      });

      if (error) {
        handleServiceError(error, 'createAuthUser');
      }

      serviceLogger.log('🔥 Auth user created successfully:', data.user?.id);
      return data;
    } catch (error) {
      handleServiceError(error, 'createAuthUser');
    }
  },

  async toggleUserAuthStatus(userId: string, isActive: boolean) {
    serviceLogger.log('🔥 Toggling user auth status:', { userId, isActive });

    try {
      // Note: In production, this would require admin privileges
      // For now, we'll just log the action as the actual auth user status
      // is controlled by the is_active field in the profiles table
      serviceLogger.log('🔥 Auth status toggle logged for user:', userId);
    } catch (error) {
      handleServiceError(error, 'toggleUserAuthStatus');
    }
  },

  async deleteAuthUser(userId: string) {
    serviceLogger.log('🔥 Deleting auth user:', userId);

    try {
      // Note: In production, this would require admin privileges
      // For now, we'll just log the action as the actual user deletion
      // is handled by the profile deletion with cascade
      serviceLogger.log('🔥 Auth user deletion logged for:', userId);
    } catch (error) {
      handleServiceError(error, 'deleteAuthUser');
    }
  },

  async sendPasswordResetEmail(email: string) {
    serviceLogger.log('🔥 Sending password reset email to:', email);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        handleServiceError(error, 'sendPasswordResetEmail: Supabase error');
      }

      serviceLogger.log('🔥 Password reset email sent successfully');
    } catch (error) {
      handleServiceError(error, 'sendPasswordResetEmail');
    }
  }
};
